import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  Globe, BarChart3, Settings2, Bot,
  ArrowRight, Check, ArrowUpRight,
  Search, Users, TrendingUp, AlertTriangle,
  Workflow, Database, BrainCircuit, LineChart,
  LayoutDashboard, MessageSquare
} from "lucide-react";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.5 }
};

const services = [
  {
    icon: Globe,
    title: "Création de site internet professionnel",
    desc: "Un site internet est votre vitrine numérique. Nous concevons des sites modernes, rapides et optimisés pour attirer vos clients et présenter votre savoir-faire.",
    features: [
      "Design professionnel adapté à votre métier",
      "Optimisé pour le référencement (SEO)",
      "Responsive mobile et tablette",
      "Formulaires de contact et demandes de devis",
      "Intégration avec vos outils existants",
      "Maintenance et mises à jour incluses"
    ],
    color: "from-blue-500/20 to-teal-500/20"
  },
  {
    icon: BarChart3,
    title: "Diagnostic stratégique de l'activité",
    desc: "Nous analysons en profondeur votre organisation pour identifier les points de friction, les outils manquants et les opportunités de croissance rapide.",
    features: [
      "Analyse de votre organisation actuelle",
      "Audit de vos outils numériques",
      "Identification des goulots d'étranglement",
      "Cartographie des opportunités de croissance",
      "Plan d'action concret et priorisé",
      "Recommandations chiffrées"
    ],
    subItems: [
      { icon: Search, label: "Organisation" },
      { icon: Settings2, label: "Outils digitaux" },
      { icon: TrendingUp, label: "Opportunités" },
      { icon: AlertTriangle, label: "Inefficacités" },
    ],
    color: "from-emerald-500/20 to-cyan-500/20"
  },
  {
    icon: Settings2,
    title: "Mise en place d'outils de gestion",
    desc: "Nous déployons les outils adaptés à votre activité pour structurer vos processus, suivre vos clients et piloter votre entreprise efficacement.",
    features: [
      "CRM adapté à votre secteur",
      "Automatisation des workflows",
      "Gestion de la relation client",
      "Tableaux de bord personnalisés",
      "Formation et accompagnement",
      "Intégration avec vos outils existants"
    ],
    subItems: [
      { icon: Users, label: "CRM" },
      { icon: Workflow, label: "Workflows" },
      { icon: LayoutDashboard, label: "Dashboards" },
      { icon: MessageSquare, label: "Gestion clients" },
    ],
    color: "from-violet-500/20 to-blue-500/20"
  },
  {
    icon: Bot,
    title: "Automatisations et solutions IA",
    desc: "Des solutions sur mesure pour automatiser les tâches chronophages et exploiter l'intelligence artificielle au service de votre productivité et rentabilité.",
    features: [
      "Assistants IA personnalisés",
      "Workflows automatisés",
      "Bases de données sur mesure",
      "Business intelligence et reporting",
      "Chatbots et réponses automatiques",
      "Intégrations API personnalisées"
    ],
    subItems: [
      { icon: BrainCircuit, label: "Assistants IA" },
      { icon: Workflow, label: "Automatisations" },
      { icon: Database, label: "Bases de données" },
      { icon: LineChart, label: "Business intelligence" },
    ],
    color: "from-teal-500/20 to-emerald-500/20"
  }
];

export default function ServicesPage() {
  return (
    <div data-testid="services-page">
      {/* Header */}
      <section className="pt-32 pb-16 px-6 md:px-12 lg:px-16 bg-[#0B1F3B] relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#59C3A6]/5 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div {...fadeUp}>
            <span className="text-[#59C3A6] text-sm font-semibold uppercase tracking-wider">Nos services</span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mt-3 mb-6 text-white">
              Des solutions adaptées{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#59C3A6] to-[#4AB095]">
                à votre métier
              </span>
            </h1>
            <p className="text-slate-300 text-base md:text-lg max-w-2xl leading-relaxed">
              Chaque entrepreneur a des besoins uniques. Nous proposons des services modulables pour accompagner votre transformation digitale à chaque étape.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services detail */}
      <section className="pb-20 md:pb-32 px-6 md:px-12 lg:px-16 bg-[#0B1F3B]">
        <div className="max-w-7xl mx-auto space-y-12">
          {services.map((service, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              data-testid={`service-detail-${i}`}
              className="rounded-2xl bg-[#112A4F]/30 border border-white/5 overflow-hidden"
            >
              <div className="p-8 md:p-10 lg:p-12">
                <div className="flex flex-col lg:flex-row gap-10">
                  {/* Left */}
                  <div className="lg:w-1/2">
                    <div className="w-14 h-14 rounded-xl bg-[#59C3A6]/10 flex items-center justify-center mb-5">
                      <service.icon size={28} className="text-[#59C3A6]" />
                    </div>
                    <h2 className="font-heading text-2xl md:text-3xl font-semibold text-white mb-4">{service.title}</h2>
                    <p className="text-slate-300 text-base leading-relaxed mb-6">{service.desc}</p>

                    {service.subItems && (
                      <div className="grid grid-cols-2 gap-3 mb-6">
                        {service.subItems.map((sub, j) => (
                          <div key={j} className="flex items-center gap-3 p-3 rounded-lg bg-[#0B1F3B]/60 border border-white/5">
                            <sub.icon size={16} className="text-[#59C3A6]" />
                            <span className="text-slate-300 text-sm">{sub.label}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Right - Features */}
                  <div className="lg:w-1/2">
                    <div className="bg-[#0B1F3B]/60 rounded-xl p-6 border border-white/5">
                      <h3 className="font-heading text-sm font-semibold text-[#59C3A6] uppercase tracking-wider mb-4">Ce qui est inclus</h3>
                      <ul className="space-y-3">
                        {service.features.map((feature, j) => (
                          <li key={j} className="flex items-start gap-3">
                            <Check size={16} className="text-[#59C3A6] mt-0.5 shrink-0" />
                            <span className="text-slate-300 text-sm">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-16 bg-[#071428]">
        <motion.div {...fadeUp} className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-5">
            Un besoin spécifique ?
          </h2>
          <p className="text-slate-300 text-base md:text-lg mb-8 leading-relaxed">
            Chaque situation est unique. Discutons ensemble pour définir la solution la plus adaptée à votre activité.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/contact">
              <Button
                data-testid="services-cta"
                className="bg-[#59C3A6] text-[#0B1F3B] hover:bg-[#4AB095] font-semibold px-8 py-3 h-auto rounded-full transition-all duration-300 hover:scale-105 shadow-[0_0_30px_rgba(89,195,166,0.25)] text-base"
              >
                Demander un diagnostic <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
            <a href="https://calendly.com/finoveo-conseil/30min" target="_blank" rel="noopener noreferrer">
              <Button
                variant="outline"
                className="border-[#59C3A6]/30 text-[#59C3A6] hover:bg-[#59C3A6]/10 hover:text-[#59C3A6] px-8 py-3 h-auto rounded-full transition-all duration-300 text-base bg-transparent"
              >
                Réserver un appel <ArrowUpRight className="ml-1" size={16} />
              </Button>
            </a>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
